// ── certificates-helpers: module access + row formatting (FR-CERT, NOW LIVE) ─
// The certificate routes are NOW LIVE: GET /certificates (view.certificates,
// scope-isolated — a participant sees ONLY their own), GET /certificates/:id, and
// POST /certificates issue (issue.certificates = SA/Admin only + a UUID
// Idempotency-Key). A certificate `artifactRef` of null means the rendered
// document is PENDING — we render an honest "document pending" state and NEVER a
// fabricated download link.
//
// PARTICIPANT: certificates are scope-isolated server-side. The admin
// /certificates surface stays STAFF-only at the route; a participant's own
// certificate appears on their dashboard certificate section (own-scope read).

import type { CoachingRole } from "../../domain/roles";
import type { CoachingCertificateDto } from "../../services/coaching-readiness-dtos";

// MUST equal the certificates admin route's roles (adminRoles).
export const CERTIFICATES_MODULE_ROLES: readonly CoachingRole[] = ["super_admin", "admin"];

export function canAccessCertificatesModule(role: CoachingRole): boolean {
  return CERTIFICATES_MODULE_ROLES.includes(role);
}

/** Issuing is SA/Admin-only (issue.certificates, migration 0224). */
export function canIssueCertificates(role: CoachingRole): boolean {
  return role === "super_admin" || role === "admin";
}

/** A certificate has a downloadable document only when artifactRef is present. */
export function hasCertificateArtifact(certificate: { artifactRef: string | null }): boolean {
  return typeof certificate.artifactRef === "string" && certificate.artifactRef.trim().length > 0;
}

/** Neutral display for a missing optional field — never a fabricated value. */
export function orDash(value: string | null | undefined): string {
  const trimmed = (value ?? "").trim();
  return trimmed.length > 0 ? trimmed : "—";
}

export function certificateStatusTone(status: string): "success" | "warning" | "danger" | "default" {
  switch (status) {
    case "issued":
    case "delivered":
      return "success";
    case "revoked":
      return "danger";
    case "pending":
    case "issuing":
      return "warning";
    default:
      return "default";
  }
}

export type CertificateRow = CoachingCertificateDto & Record<string, unknown>;
